import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Navigate, Link } from "react-router-dom";
import { api, formatError, resolvePhotoUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Trash2, ArrowUp, ArrowDown, Save, Send, Download, Copy,
  Upload, Building2, User, Receipt, ListPlus, StickyNote, Landmark, Loader2, Check, ChevronDown,
} from "@/lib/icons";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import InvoicePreview from "@/components/InvoicePreview";
import { useSeo } from "@/lib/seo";

const CURRENCIES = ["INR", "USD", "EUR", "GBP", "AUD", "CAD", "AED", "SGD"];
const INVOICE_ACCENT_PRESETS = [
  { name: "Violet", value: "#7F00FF" },
  { name: "Indigo", value: "#4F46E5" },
  { name: "Navy", value: "#1E3A5F" },
  { name: "Teal", value: "#0F766E" },
  { name: "Emerald", value: "#047857" },
  { name: "Burgundy", value: "#9F1239" },
  { name: "Charcoal", value: "#27272A" },
];
const BLANK_ITEM = { description: "", quantity: 1, unit_price: 0, discount: 0, tax_percent: 0 };
const AUTOSAVE_DELAY = 1500;

const emptyForm = () => ({
  business: { name: "", owner_name: "", logo_url: "", phone: "", email: "", address: "", gst_number: "", website: "" },
  customer: { name: "", phone: "", email: "", address: "", company_name: "", gst_number: "" },
  invoice_date: new Date().toISOString().slice(0, 10),
  due_date: new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10),
  currency: "INR",
  payment_terms: "",
  status: "draft",
  project_name: "",
  event_date: "",
  event_location: "",
  items: [{ ...BLANK_ITEM }],
  paid_amount: 0,
  notes: "",
  terms: "",
  signature_url: "",
  qr_image_url: "",
  bank_details: { account_name: "", account_number: "", ifsc: "", bank_name: "" },
  upi_id: "",
  show_status_badge: false,
  accent_color: "#7F00FF",
});

function computeItem(item) {
  const qty = Number(item.quantity) || 0;
  const price = Number(item.unit_price) || 0;
  const lineSubtotal = Math.max(0, qty * price);
  const discount = Math.min(lineSubtotal, Math.max(0, Number(item.discount) || 0));
  const taxPercent = Math.max(0, Number(item.tax_percent) || 0);
  const base = lineSubtotal - discount;
  const amount = Math.round((base + base * (taxPercent / 100)) * 100) / 100;
  return { ...item, quantity: qty, unit_price: price, discount, tax_percent: taxPercent, amount };
}
function computeTotals(items, paidAmount) {
  const subtotal = items.reduce((s, i) => s + (Number(i.quantity) || 0) * (Number(i.unit_price) || 0), 0);
  const discountTotal = items.reduce((s, i) => {
    const lineSubtotal = Math.max(0, (Number(i.quantity) || 0) * (Number(i.unit_price) || 0));
    return s + Math.min(lineSubtotal, Math.max(0, Number(i.discount) || 0));
  }, 0);
  const taxTotal = items.reduce((s, i) => {
    const base = Math.max(0, (Number(i.quantity) || 0) * (Number(i.unit_price) || 0) - (Number(i.discount) || 0));
    return s + base * ((Number(i.tax_percent) || 0) / 100);
  }, 0);
  const grandTotal = Math.round(items.reduce((sum, item) => sum + (Number(item.amount) || 0), 0) * 100) / 100;
  const paid = Math.min(grandTotal, Math.max(0, Number(paidAmount) || 0));
  return {
    subtotal: round2(subtotal), discount_total: round2(discountTotal), tax_total: round2(taxTotal),
    grand_total: grandTotal, paid_amount: round2(paid), balance_due: round2(Math.max(0, grandTotal - paid)),
  };
}
const round2 = (n) => Math.round(n * 100) / 100;

const inputCls = "h-10 bg-white/5 border-white/10 text-white placeholder:text-white/35";
const textareaCls = "min-h-[80px] bg-white/5 border-white/10 text-white placeholder:text-white/35 text-sm";

export default function InvoiceEditor() {
  const { invoiceId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isNew = !invoiceId;
  useSeo({ title: "Invoice Editor | PRONET", noindex: true });

  const [form, setForm] = useState(emptyForm());
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [currentId, setCurrentId] = useState(invoiceId || null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [saveState, setSaveState] = useState(""); // "" | "saving" | "saved"
  const [errors, setErrors] = useState([]);
  const [openSections, setOpenSections] = useState({
    business: true, customer: true, invoice: true, items: true, notes: true, bank: true,
  });
  const toggleSection = (id) => setOpenSections((s) => ({ ...s, [id]: !s[id] }));
  const dirtyRef = useRef(false);
  const autosaveTimer = useRef(null);

  useEffect(() => {
    if (isNew) {
      api.get("/member/business-profile").then((r) => {
        const p = r.data || {};
        setForm((f) => ({
          ...f,
          business: {
            name: p.name || "", owner_name: p.owner_name || "", logo_url: p.logo_url || "",
            phone: p.phone || "", email: p.email || "", address: p.address || "",
            gst_number: p.gst_number || "", website: p.website || "",
          },
          terms: p.terms || "",
          signature_url: p.signature_url || "",
          qr_image_url: p.qr_image_url || "",
          bank_details: p.bank_details || f.bank_details,
          upi_id: p.upi_id || "",
        }));
      }).catch(() => {});
    } else {
      api.get(`/invoices/${invoiceId}`).then((r) => {
        const d = r.data;
        setInvoiceNumber(d.invoice_number);
        setCurrentId(d.id);
        setForm({
          business: d.business || emptyForm().business,
          customer: d.customer || emptyForm().customer,
          invoice_date: (d.invoice_date || "").slice(0, 10),
          due_date: (d.due_date || "").slice(0, 10),
          currency: d.currency || "INR",
          payment_terms: d.payment_terms || "",
          status: d.status || "draft",
          project_name: d.project_name || "",
          event_date: (d.event_date || "").slice(0, 10),
          event_location: d.event_location || "",
          items: d.items?.length ? d.items : [{ ...BLANK_ITEM }],
          paid_amount: d.totals?.paid_amount || 0,
          notes: d.notes || "",
          terms: d.terms || "",
          signature_url: d.signature_url || "",
          qr_image_url: d.qr_image_url || "",
          bank_details: d.bank_details || emptyForm().bank_details,
          upi_id: d.upi_id || "",
          show_status_badge: d.show_status_badge || false,
          accent_color: /^#[0-9a-f]{6}$/i.test(d.accent_color || "") ? d.accent_color.toUpperCase() : "#7F00FF",
        });
      }).catch((e) => toast.error(formatError(e)))
        .finally(() => setLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId]);

  const items = useMemo(() => form.items.map(computeItem), [form.items]);
  const totals = useMemo(() => computeTotals(items, form.paid_amount), [items, form.paid_amount]);

  const set = (path) => (value) => {
    dirtyRef.current = true;
    setForm((f) => {
      const next = { ...f };
      if (path.includes(".")) {
        const [a, b] = path.split(".");
        next[a] = { ...next[a], [b]: value };
      } else {
        next[path] = value;
      }
      return next;
    });
  };
  const setItem = (i, key) => (value) => {
    dirtyRef.current = true;
    setForm((f) => {
      const items2 = [...f.items];
      items2[i] = { ...items2[i], [key]: value };
      return { ...f, items: items2 };
    });
  };
  const addItem = () => { dirtyRef.current = true; setForm((f) => ({ ...f, items: [...f.items, { ...BLANK_ITEM }] })); };
  const removeItem = (i) => { dirtyRef.current = true; setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) })); };
  const moveItem = (i, dir) => {
    dirtyRef.current = true;
    setForm((f) => {
      const arr = [...f.items];
      const j = i + dir;
      if (j < 0 || j >= arr.length) return f;
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return { ...f, items: arr };
    });
  };

  const validate = (status) => {
    if (status === "draft") return [];
    const errs = [];
    if (!form.customer.name.trim()) errs.push("Customer name is required");
    if (!items.length) errs.push("At least one line item is required");
    if (!form.invoice_date) errs.push("Invoice date is required");
    if (!form.due_date) errs.push("Due date is required");
    if (totals.grand_total <= 0) errs.push("Invoice total must be greater than zero");
    return errs;
  };

  const payload = (status) => ({
    ...form,
    status,
    invoice_date: form.invoice_date || null,
    due_date: form.due_date || null,
    event_date: form.event_date || null,
    items: form.items,
  });

  const save = useCallback(async (status, { silent = false } = {}) => {
    const errs = validate(status);
    if (errs.length) {
      setErrors(errs);
      if (!silent) toast.error(errs[0]);
      return null;
    }
    setErrors([]);
    if (!silent) setSaving(true);
    setSaveState("saving");
    try {
      let res;
      if (currentId) {
        res = await api.put(`/invoices/${currentId}`, payload(status));
      } else {
        res = await api.post("/invoices", payload(status));
        setCurrentId(res.data.id);
        nav(`/account/invoices/${res.data.id}`, { replace: true });
      }
      setInvoiceNumber(res.data.invoice_number);
      setSaveState("saved");
      dirtyRef.current = false;
      if (!silent) toast.success(status === "draft" ? "Draft saved" : "Invoice saved");
      return res.data;
    } catch (e) {
      setSaveState("");
      if (!silent) toast.error(formatError(e));
      return null;
    } finally {
      if (!silent) setSaving(false);
    }
  }, [currentId, form, items, totals]); // eslint-disable-line react-hooks/exhaustive-deps

  // Autosave drafts only, debounced.
  useEffect(() => {
    if (loading || form.status !== "draft") return;
    if (!dirtyRef.current) return;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => { save("draft", { silent: true }); }, AUTOSAVE_DELAY);
    return () => clearTimeout(autosaveTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, loading]);

  const downloadPdfFor = async (id, number) => {
    if (!id) { toast.error("Save the invoice first"); return; }
    try {
      const res = await api.post(`/invoices/${id}/download`, null, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${number || "invoice"}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatError(e)); }
  };
  const downloadPdf = () => downloadPdfFor(currentId, invoiceNumber);

  // Save & Mark Sent hands the client a document, so it downloads the PDF
  // right after saving — Save Draft does not.
  const sendInvoice = async () => {
    const saved = await save(form.status === "paid" ? "paid" : "sent");
    if (saved) {
      setForm((f) => ({ ...f, status: saved.status }));
      await downloadPdfFor(saved.id, saved.invoice_number);
    }
  };

  const duplicateInvoice = async () => {
    if (!currentId) return;
    try {
      const { data } = await api.post(`/invoices/${currentId}/duplicate`);
      toast.success(`Duplicated as ${data.invoice_number}`);
      nav(`/account/invoices/${data.id}`);
    } catch (e) { toast.error(formatError(e)); }
  };

  const deleteInvoice = async () => {
    if (!currentId || !window.confirm(`Delete invoice ${invoiceNumber}? This can't be undone.`)) return;
    try {
      await api.delete(`/invoices/${currentId}`);
      toast.success("Invoice deleted");
      nav("/account");
    } catch (e) { toast.error(formatError(e)); }
  };

  const uploadAsset = async (kind, file) => {
    if (!file || !["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(file.type.toLowerCase())) {
      toast.error("Only JPG, PNG or WEBP files are allowed");
      return;
    }
    if (file.size > 12 * 1024 * 1024) {
      toast.error("Image must be 12 MB or less");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/member/business-profile/${kind}`, fd);
      if (!data?.url) throw new Error("The server did not return the uploaded image URL");
      if (kind === "logo") set("business.logo_url")(data.url);
      if (kind === "signature") set("signature_url")(data.url);
      if (kind === "qr-image") set("qr_image_url")(data.url);
      toast.success("Uploaded");
    } catch (e) { toast.error(formatError(e)); }
  };

  const removeAsset = async (kind) => {
    try {
      await api.delete(`/member/business-profile/${kind}`);
      if (kind === "logo") set("business.logo_url")("");
      if (kind === "signature") set("signature_url")("");
      if (kind === "qr-image") set("qr_image_url")("");
      toast.success("Removed");
    } catch (e) { toast.error(formatError(e)); }
  };

  if (user === null) return <div className="container-tight py-20 text-white/50">Loading…</div>;
  if (!user || user.role !== "member") return <Navigate to="/login" replace />;
  if (loading) return <div className="container-tight py-20 text-white/50">Loading invoice…</div>;

  return (
    <div className="relative container-wide py-10 md:py-14">
      <div className="blobs-layer"><div className="blob blob-violet w-[500px] h-[500px] -top-40 -left-20 opacity-20" /></div>
      <div className="relative z-10">
        {/* TOP BAR */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <Link to="/account" className="btn-ghost inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm text-white">
              <ArrowLeft size={14} /> Back
            </Link>
            <div>
              <div className="font-display text-2xl text-white">{isNew && !currentId ? "New invoice" : invoiceNumber}</div>
              <SaveIndicator state={saveState} />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {currentId && <button onClick={duplicateInvoice} className="btn-ghost inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm text-white"><Copy size={13} /> Duplicate</button>}
            {currentId && <button onClick={downloadPdf} className="btn-ghost inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm text-white"><Download size={13} /> Download PDF</button>}
            {currentId && <button onClick={deleteInvoice} className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm text-red-300 border border-red-500/20 hover:bg-red-500/10"><Trash2 size={13} /> Delete</button>}
            <SaveButtonsRow saving={saving} status={form.status} onSaveDraft={() => save("draft")} onSend={sendInvoice} testidSuffix="top" />
          </div>
        </div>

        {errors.length > 0 && (
          <div className="mb-6 rounded-2xl border border-red-500/25 bg-red-500/[0.06] p-4 text-sm text-red-200" data-testid="invoice-errors">
            <ul className="list-disc pl-5 space-y-0.5">{errors.map((e) => <li key={e}>{e}</li>)}</ul>
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-8 items-start">
          {/* FORM */}
          <div className="space-y-6 min-w-0">
            <Section id="business" open={openSections.business} onToggle={toggleSection} icon={Building2} title="Business information">
              <div className="mb-4">
                <AssetField label="Logo" url={resolvePhotoUrl(form.business.logo_url)}
                  onFile={(f) => uploadAsset("logo", f)} onRemove={() => removeAsset("logo")}
                  previewClass="h-12 w-12 rounded object-contain bg-white/5" testidPrefix="invoice-logo" />
              </div>
              <Grid>
                <Field label="Business name"><Input className={inputCls} value={form.business.name} onChange={(e) => set("business.name")(e.target.value)} /></Field>
                <Field label="Owner name"><Input className={inputCls} value={form.business.owner_name} onChange={(e) => set("business.owner_name")(e.target.value)} /></Field>
                <Field label="Phone"><Input className={inputCls} value={form.business.phone} onChange={(e) => set("business.phone")(e.target.value)} /></Field>
                <Field label="Email"><Input className={inputCls} value={form.business.email} onChange={(e) => set("business.email")(e.target.value)} /></Field>
                <Field label="GST number"><Input className={inputCls} value={form.business.gst_number} onChange={(e) => set("business.gst_number")(e.target.value)} /></Field>
                <Field label="Website"><Input className={inputCls} value={form.business.website} onChange={(e) => set("business.website")(e.target.value)} /></Field>
                <Field label="Address" span><Textarea className={textareaCls} rows={2} value={form.business.address} onChange={(e) => set("business.address")(e.target.value)} /></Field>
              </Grid>
            </Section>

            <Section id="customer" open={openSections.customer} onToggle={toggleSection} icon={User} title="Customer information">
              <Grid>
                <Field label="Customer name" required><Input className={inputCls} value={form.customer.name} onChange={(e) => set("customer.name")(e.target.value)} data-testid="invoice-customer-name" /></Field>
                <Field label="Company name"><Input className={inputCls} value={form.customer.company_name} onChange={(e) => set("customer.company_name")(e.target.value)} /></Field>
                <Field label="Phone"><Input className={inputCls} value={form.customer.phone} onChange={(e) => set("customer.phone")(e.target.value)} /></Field>
                <Field label="Email"><Input className={inputCls} value={form.customer.email} onChange={(e) => set("customer.email")(e.target.value)} /></Field>
                <Field label="GST number"><Input className={inputCls} value={form.customer.gst_number} onChange={(e) => set("customer.gst_number")(e.target.value)} /></Field>
                <Field label="Address" span><Textarea className={textareaCls} rows={2} value={form.customer.address} onChange={(e) => set("customer.address")(e.target.value)} /></Field>
              </Grid>
            </Section>

            <Section id="invoice" open={openSections.invoice} onToggle={toggleSection} icon={Receipt} title="Invoice information">
              <Grid>
                <Field label="Invoice date" required><Input type="date" className={inputCls} value={form.invoice_date} onChange={(e) => set("invoice_date")(e.target.value)} data-testid="invoice-date" /></Field>
                <Field label="Due date" required><Input type="date" className={inputCls} value={form.due_date} onChange={(e) => set("due_date")(e.target.value)} data-testid="invoice-due-date" /></Field>
                <Field label="Currency">
                  <Select value={form.currency} onValueChange={set("currency")}>
                    <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
                    <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Payment terms"><Input className={inputCls} placeholder="e.g. Net 15" value={form.payment_terms} onChange={(e) => set("payment_terms")(e.target.value)} /></Field>
                <Field label="Project / event name"><Input className={inputCls} value={form.project_name} onChange={(e) => set("project_name")(e.target.value)} /></Field>
                <Field label="Event date"><Input type="date" className={inputCls} value={form.event_date} onChange={(e) => set("event_date")(e.target.value)} /></Field>
                <Field label="Event location" span><Input className={inputCls} value={form.event_location} onChange={(e) => set("event_location")(e.target.value)} /></Field>
              </Grid>
              <div className="mt-4 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-4" data-testid="invoice-accent-picker">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="text-sm text-white/80">Invoice accent color</div>
                    <div className="mt-0.5 text-xs text-white/40">Used in both the live preview and downloaded PDF.</div>
                  </div>
                  <div className="flex items-center gap-2 rounded-lg border border-white/10 bg-black/20 px-2 py-1.5">
                    <input
                      type="color"
                      value={form.accent_color}
                      onChange={(e) => set("accent_color")(e.target.value.toUpperCase())}
                      className="h-7 w-9 cursor-pointer rounded border-0 bg-transparent p-0"
                      aria-label="Custom invoice accent color"
                      data-testid="invoice-custom-color"
                    />
                    <span className="font-mono text-xs text-white/70">{form.accent_color}</span>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-2" role="radiogroup" aria-label="Invoice accent color presets">
                  {INVOICE_ACCENT_PRESETS.map((color) => {
                    const selected = form.accent_color.toUpperCase() === color.value;
                    return (
                      <button
                        key={color.value}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        title={color.name}
                        onClick={() => set("accent_color")(color.value)}
                        className={`flex h-9 items-center gap-2 rounded-lg border px-3 text-xs transition-colors ${selected ? "border-white/70 bg-white/10 text-white" : "border-white/10 text-white/55 hover:border-white/30 hover:text-white"}`}
                        data-testid={`invoice-color-${color.name.toLowerCase()}`}
                      >
                        <span className="h-4 w-4 rounded-full border border-white/20" style={{ backgroundColor: color.value }} />
                        {color.name}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="mt-4 flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
                <div>
                  <div className="text-sm text-white/80">Show status badge on invoice</div>
                  <div className="text-xs text-white/40 mt-0.5">A small DRAFT / SENT / PAID stamp in the corner. Off by default.</div>
                </div>
                <Switch data-testid="invoice-status-badge-toggle" checked={form.show_status_badge} onCheckedChange={set("show_status_badge")} />
              </div>
            </Section>

            <Section id="items" open={openSections.items} onToggle={toggleSection} icon={ListPlus} title="Line items">
              <div className="space-y-3">
                {form.items.map((item, i) => (
                  <div key={i} className="glass rounded-xl p-3" data-testid={`invoice-item-${i}`}>
                    <div className="flex items-start gap-2">
                      <Textarea className={`${textareaCls} flex-1 min-h-[40px]`} rows={1} placeholder="Description"
                        value={item.description} onChange={(e) => setItem(i, "description")(e.target.value)} />
                      <div className="flex flex-col gap-1 shrink-0">
                        <button onClick={() => moveItem(i, -1)} disabled={i === 0} className="p-1 rounded text-white/40 hover:text-white disabled:opacity-30"><ArrowUp size={13} /></button>
                        <button onClick={() => moveItem(i, 1)} disabled={i === form.items.length - 1} className="p-1 rounded text-white/40 hover:text-white disabled:opacity-30"><ArrowDown size={13} /></button>
                      </div>
                      <button onClick={() => removeItem(i)} className="p-2 rounded-lg text-red-300 hover:bg-red-500/10 shrink-0"><Trash2 size={14} /></button>
                    </div>
                    <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
                      <MiniField label="Qty"><Input type="number" className={inputCls} value={item.quantity} onChange={(e) => setItem(i, "quantity")(e.target.value)} /></MiniField>
                      <MiniField label="Unit price"><Input type="number" className={inputCls} value={item.unit_price} onChange={(e) => setItem(i, "unit_price")(e.target.value)} /></MiniField>
                      <MiniField label="Discount"><Input type="number" className={inputCls} value={item.discount} onChange={(e) => setItem(i, "discount")(e.target.value)} /></MiniField>
                      <MiniField label="Tax %"><Input type="number" className={inputCls} value={item.tax_percent} onChange={(e) => setItem(i, "tax_percent")(e.target.value)} /></MiniField>
                    </div>
                  </div>
                ))}
                <button onClick={addItem} data-testid="invoice-add-item"
                  className="btn-ghost inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm text-white">
                  <Plus size={13} /> Add item
                </button>
              </div>
              <div className="mt-4">
                <Field label="Amount paid so far">
                  <Input type="number" className={`${inputCls} max-w-[180px]`} value={form.paid_amount} onChange={(e) => set("paid_amount")(e.target.value)} />
                </Field>
              </div>
            </Section>

            <Section id="notes" open={openSections.notes} onToggle={toggleSection} icon={StickyNote} title="Notes & terms">
              <Grid>
                <Field label="Notes" span><Textarea className={textareaCls} rows={3} value={form.notes} onChange={(e) => set("notes")(e.target.value)} /></Field>
                <Field label="Terms & conditions" span><Textarea className={textareaCls} rows={3} value={form.terms} onChange={(e) => set("terms")(e.target.value)} /></Field>
              </Grid>
              <div className="mt-4 flex flex-col flex-wrap gap-4">
                <AssetField label="Authorized signature" url={resolvePhotoUrl(form.signature_url)}
                  onFile={(f) => uploadAsset("signature", f)} onRemove={() => removeAsset("signature")}
                  previewClass="h-10 object-contain bg-white/5 rounded px-2" testidPrefix="invoice-signature" />
                <AssetField label="Payment QR image" url={resolvePhotoUrl(form.qr_image_url)}
                  onFile={(f) => uploadAsset("qr-image", f)} onRemove={() => removeAsset("qr-image")}
                  previewClass="h-12 w-12 rounded object-contain bg-white/5" testidPrefix="invoice-qr" />
              </div>
            </Section>

            <Section id="bank" open={openSections.bank} onToggle={toggleSection} icon={Landmark} title="Bank details & UPI">
              <Grid>
                <Field label="Account name"><Input className={inputCls} value={form.bank_details.account_name} onChange={(e) => set("bank_details.account_name")(e.target.value)} /></Field>
                <Field label="Account number"><Input className={inputCls} value={form.bank_details.account_number} onChange={(e) => set("bank_details.account_number")(e.target.value)} /></Field>
                <Field label="IFSC"><Input className={inputCls} value={form.bank_details.ifsc} onChange={(e) => set("bank_details.ifsc")(e.target.value)} /></Field>
                <Field label="Bank name"><Input className={inputCls} value={form.bank_details.bank_name} onChange={(e) => set("bank_details.bank_name")(e.target.value)} /></Field>
                <Field label="UPI ID"><Input className={inputCls} value={form.upi_id} onChange={(e) => set("upi_id")(e.target.value)} /></Field>
              </Grid>
            </Section>

            <div className="glass rounded-2xl p-5 flex items-center justify-between gap-3">
              <div className="text-xs text-white/40">Everything is ready. Save when you are finished.</div>
              <SaveButtonsRow saving={saving} status={form.status} onSaveDraft={() => save("draft")} onSend={sendInvoice} testidSuffix="bottom" />
            </div>
          </div>

          {/* LIVE PREVIEW */}
          <div className="lg:sticky lg:top-6 min-w-0">
            <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-white/40">Live preview</div>
            <InvoicePreview form={{ ...form, items }} totals={totals} invoiceNumber={invoiceNumber || "Draft"} />
          </div>
        </div>
      </div>
    </div>
  );
}

function SaveIndicator({ state }) {
  if (state === "saving") return <div className="text-xs text-white/40 inline-flex items-center gap-1 mt-1"><Loader2 size={11} className="animate-spin" /> Saving…</div>;
  if (state === "saved") return <div className="text-xs text-emerald-300/80 inline-flex items-center gap-1 mt-1"><Check size={11} /> Saved</div>;
  return <div className="text-xs text-white/30 mt-1">Unsaved</div>;
}

function SaveButtonsRow({ saving, status, onSaveDraft, onSend, testidSuffix }) {
  return (
    <div className="flex flex-wrap gap-2">
      <button onClick={onSaveDraft} disabled={saving} data-testid={`invoice-save-draft-${testidSuffix}`}
        className="btn-ghost inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm text-white disabled:opacity-60">
        <Save size={13} /> Save Draft
      </button>
      <button onClick={onSend} disabled={saving} data-testid={`invoice-save-send-${testidSuffix}`}
        className="btn-glow inline-flex items-center gap-1.5 rounded-full px-5 py-2 text-sm font-semibold text-white disabled:opacity-60">
        <Send size={13} /> {status === "paid" ? "Save" : "Save & Mark Sent"}
      </button>
    </div>
  );
}

// Collapsible accordion panel — click the header to open/close. Sections
// start open; nothing auto-collapses (that was tried and felt twitchy while
// tabbing between fields), so the only way a section closes is a click.
function Section({ id, icon: Icon, title, open, onToggle, children }) {
  return (
    <div className="glass rounded-2xl overflow-hidden">
      <button
        type="button"
        onClick={() => onToggle(id)}
        data-testid={`invoice-section-toggle-${id}`}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-2 p-5 text-left"
      >
        <div className="flex items-center gap-2 font-display text-lg text-white">
          <Icon size={16} className="text-fuchsia-300" /> {title}
        </div>
        <ChevronDown size={16} className={`text-white/40 transition-transform duration-200 shrink-0 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-5 pb-5">{children}</div>}
    </div>
  );
}
function Grid({ children }) { return <div className="grid sm:grid-cols-2 gap-3">{children}</div>; }
function Field({ label, required, span, children }) {
  return (
    <div className={span ? "sm:col-span-2" : ""}>
      <label className="overline text-white/50 text-[10px]">{label} {required && <span className="text-fuchsia-400">*</span>}</label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
function MiniField({ label, children }) {
  return (
    <div>
      <label className="text-[10px] text-white/40">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
// Upload widget with a live preview plus Change/Remove — same pattern used
// for the site logo and ID card artwork elsewhere in Admin CMS.
function AssetField({ label, url, onFile, onRemove, previewClass, testidPrefix }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = async (f) => {
    if (!f) return;
    setBusy(true);
    try { await onFile(f); }
    finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  const remove = async () => {
    if (!window.confirm(`Remove ${label.toLowerCase()}?`)) return;
    setBusy(true);
    try { await onRemove(); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex flex-wrap items-center gap-3">
      {url && <img src={url} alt={label} className={previewClass} data-testid={`${testidPrefix}-preview`} />}
      <input ref={ref} type="file" accept="image/jpeg,image/jpg,image/png,image/webp" className="hidden"
        data-testid={`${testidPrefix}-input`}
        onChange={(e) => pick(e.target.files?.[0])} />
      <button type="button" onClick={() => ref.current?.click()} disabled={busy}
        data-testid={`${testidPrefix}-upload-btn`}
        className="btn-ghost inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold text-white disabled:opacity-60">
        <Upload size={12} /> {url ? "Change" : "Upload"} {label}
      </button>
      {url && (
        <button type="button" onClick={remove} disabled={busy}
          data-testid={`${testidPrefix}-remove-btn`}
          className="inline-flex items-center gap-1.5 rounded-full border border-red-500/20 px-4 py-2 text-xs font-semibold text-red-300 hover:bg-red-500/10 disabled:opacity-60">
          <Trash2 size={12} /> Remove
        </button>
      )}
    </div>
  );
}
